import { type FormEvent } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { INVENTORY_PATHS, tInventory, type UnitOption } from "@/features/staff/inventory/inventory"

type FormShape = {
  name: string
  unit: string
  reorder_point: string
}

/**
 * Creating a supply, with an explicit submit rather than inline fields.
 *
 * The name and the reorder point are light operational fields and edit inline on
 * the list. The unit is not: `Location::Supply` refuses to change it once the
 * supply has a single movement, because nothing in this feature converts and
 * reinterpreting a balance from grams to kilos does not raise an error — it
 * silently multiplies every historical figure by a thousand. A field whose first
 * value is close to permanent belongs in a form somebody submits on purpose,
 * beside the hint that says so, not in a select that commits the moment it is
 * brushed.
 */
export function SupplyForm({ units, onDone }: { units: UnitOption[]; onDone: () => void }) {
  const { data, setData, post, errors, processing } = useForm<FormShape>({
    name: "",
    unit: units[0]?.value ?? "gram",
    reorder_point: "",
  })

  // `record.errors.to_hash(true)` keys a model-level error under `base`, which
  // is not a field on this form and so is absent from `useForm`'s inferred error
  // map. Dropping it would swallow every refusal that is not about one input.
  const baseError = (errors as Record<string, string | string[] | undefined>).base

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (data.name.trim().length === 0) return

    post(INVENTORY_PATHS.supplies, { preserveScroll: true, onSuccess: () => onDone() })
  }

  return (
    <form className="space-y-4" onSubmit={submit} data-testid="supply-form">
      <FieldError error={baseError} />

      <div className="space-y-2">
        <Label htmlFor="supply-name">{tInventory("name_label")}</Label>
        <Input
          id="supply-name"
          value={data.name}
          placeholder={tInventory("name_placeholder")}
          onChange={(event) => setData("name", event.target.value)}
          data-testid="supply-name"
        />
        <FieldError error={errors.name} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supply-unit">{tInventory("unit_label")}</Label>
        <Select value={data.unit} onValueChange={(value) => setData("unit", value)}>
          <SelectTrigger id="supply-unit" className="w-full" data-testid="supply-unit">
            <SelectValue placeholder={tInventory("unit_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {units.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`supply-unit-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">{tInventory("unit_hint")}</p>
        <FieldError error={errors.unit} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supply-reorder-point">{tInventory("reorder_point_label")}</Label>
        <Input
          id="supply-reorder-point"
          inputMode="decimal"
          value={data.reorder_point}
          placeholder={tInventory("reorder_point_placeholder")}
          onChange={(event) => setData("reorder_point", event.target.value)}
          data-testid="supply-reorder-point"
        />
        <p className="text-muted-foreground text-xs">{tInventory("reorder_point_hint")}</p>
        <FieldError error={errors.reorder_point} />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone} data-testid="supply-form-cancel">
          {tInventory("cancel")}
        </Button>
        <Button type="submit" disabled={processing} data-testid="supply-form-submit">
          {processing ? tInventory("creating") : tInventory("create")}
        </Button>
      </div>
    </form>
  )
}
